import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../apiConfig';
import './Feedback.css';

const STEP_LABELS = {
  categorize:          'Categorize',
  requiresResponse:    'Reply check',
  draftResponse:       'Draft',
  evaluateSufficiency: 'Evaluate',
  extractInsights:     'Insights',
};

function formatAt(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatBool(val) {
  if (val === true) return 'Reply needed';
  if (val === false) return 'FYI';
  return String(val);
}

const Feedback = () => {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [text, setText]         = useState('');
  const [processing, setProcessing] = useState(false);
  const [result, setResult]     = useState(null);
  const [error, setError]       = useState(null);

  const load = () => {
    setLoading(true);
    fetch(`${API_BASE_URL}/feedback`, { credentials: 'include' })
      .then(res => res.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleProcess = () => {
    setProcessing(true);
    setResult(null);
    setError(null);
    fetch(`${API_BASE_URL}/feedback/process`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text }),
    })
      .then(async res => {
        const d = await res.json();
        if (!res.ok) throw new Error(d.error || `Request failed (${res.status})`);
        return d;
      })
      .then(d => {
        setResult(d);
        setProcessing(false);
        load(); // refresh preferences + history
      })
      .catch(err => {
        setError(err.message);
        setProcessing(false);
      });
  };

  if (loading) {
    return (
      <div className="feedback-page">
        <div className="feedback-page__head">
          <h1 className="feedback-page__title">Feedback & Learning</h1>
        </div>
        <div style={{ display: 'flex', gap: 10, color: 'var(--text-muted)', alignItems: 'center' }}>
          <div className="spinner" />
          <span>Loading…</span>
        </div>
      </div>
    );
  }

  const preferences = data?.preferences ?? {};
  const corrections = [...(data?.corrections ?? [])].reverse().slice(0, 10);
  const history = [...(data?.history ?? [])].reverse().slice(0, 10);

  return (
    <div className="feedback-page">
      <div className="feedback-page__head">
        <h1 className="feedback-page__title">Feedback & Learning</h1>
        <p className="feedback-page__subtitle">
          Correct tags inline in your inbox to teach the AI your preferences.
          Use the form below to add text feedback. Preferences are injected into every pipeline run.
        </p>
      </div>

      {/* ── Section 1: Current learned preferences ── */}
      <div className="feedback-section">
        <h2 className="feedback-section__title">Learned Preferences</h2>
        <p className="feedback-section__desc">
          These preferences are injected into each AI pipeline step before every email is processed.
        </p>
        <div className="feedback-prefs">
          {Object.entries(STEP_LABELS).map(([key, label]) => (
            <div key={key} className="feedback-pref-row">
              <span className="feedback-pref-row__step">{label}</span>
              {preferences[key]
                ? <span className="feedback-pref-row__text">{preferences[key]}</span>
                : <span className="feedback-pref-row__empty">No preference set</span>
              }
            </div>
          ))}
        </div>
      </div>

      {/* ── Section 2: Recent corrections ── */}
      <div className="feedback-section">
        <h2 className="feedback-section__title">Recent Tag Corrections</h2>
        <p className="feedback-section__desc">
          Every time you change a tag in the inbox, it's recorded here and used to detect patterns.
        </p>
        <div className="feedback-corrections">
          {corrections.length === 0 ? (
            <p className="feedback-empty">No corrections recorded yet. Edit tags in the inbox to get started.</p>
          ) : corrections.map((c, i) => (
            <div key={i} className="feedback-correction-item">
              <span className="feedback-correction-item__field">{c.field}</span>
              <span className="feedback-correction-item__from">{formatBool(c.from)}</span>
              <span className="feedback-correction-item__arrow">→</span>
              <span className="feedback-correction-item__to">{formatBool(c.to)}</span>
              <span className="feedback-correction-item__date">{formatAt(c.at)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Section 3: Submit feedback ── */}
      <div className="feedback-section">
        <h2 className="feedback-section__title">Submit Feedback</h2>
        <p className="feedback-section__desc">
          Describe what the AI is getting wrong or what you'd like it to do differently.
          This is combined with your recent tag corrections and processed by an LLM to update preferences.
        </p>
        <textarea
          className="feedback-textarea"
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder="e.g. Marketing newsletters should always be Low priority. Don't mark GitHub notifications as requiring a reply."
        />
        <div className="feedback-actions">
          <button
            className="btn btn--primary"
            onClick={handleProcess}
            disabled={processing}
          >
            {processing ? 'Processing…' : 'Process Feedback'}
          </button>
          {error && <span style={{ color: '#c5221f', fontSize: 13 }}>{error}</span>}
        </div>

        {/* ── Result panel ── */}
        {result && (
          <div className="feedback-result">
            <div className="feedback-result__summary">{result.summary}</div>
            <div className="feedback-result__prefs-title">Updated Preferences</div>
            {Object.entries(STEP_LABELS).map(([key, label]) => (
              result.learnedPreferences?.[key] ? (
                <div key={key} className="feedback-result__pref-row">
                  <span className="feedback-result__pref-step">{label}</span>
                  <span className="feedback-result__pref-text">{result.learnedPreferences[key]}</span>
                </div>
              ) : null
            ))}

            {/* Applied model changes */}
            {result.appliedModelChanges && Object.keys(result.appliedModelChanges).length > 0 && (
              <div className="feedback-model-changes feedback-model-changes--applied">
                <div className="feedback-model-changes__title">Model Changes Applied</div>
                {result.modelChangeSummary && (
                  <div className="feedback-model-changes__summary">{result.modelChangeSummary}</div>
                )}
                {Object.entries(result.appliedModelChanges).map(([step, change]) => (
                  <div key={step} className="feedback-model-change-row">
                    <span className="feedback-model-change-row__step">{STEP_LABELS[step] ?? step}</span>
                    <span className="feedback-model-change-row__from">{change.from}</span>
                    <span className="feedback-model-change-row__arrow">→</span>
                    <span className="feedback-model-change-row__to">{change.to}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Skipped / pending model changes */}
            {result.skippedModelChanges && Object.keys(result.skippedModelChanges).length > 0 && (
              <div className="feedback-model-changes feedback-model-changes--skipped">
                <div className="feedback-model-changes__title">Pending Model Changes</div>
                {Object.entries(result.skippedModelChanges).map(([step, change]) => (
                  <div key={step} className="feedback-model-change-row">
                    <span className="feedback-model-change-row__step">{STEP_LABELS[step] ?? step}</span>
                    <span className="feedback-model-change-row__to">{change.to}</span>
                    <span className="feedback-model-change-row__reason">{change.reason}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Section 4: History ── */}
      {history.length > 0 && (
        <div className="feedback-section">
          <h2 className="feedback-section__title">Feedback History</h2>
          <div className="feedback-history">
            {history.map((entry, i) => (
              <div key={i} className="feedback-history-item">
                <div className="feedback-history-item__date">{formatAt(entry.at)}</div>
                <div className="feedback-history-item__summary">{entry.summary}</div>
                {entry.text && (
                  <div className="feedback-history-item__text">"{entry.text}"</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Feedback;
